import * as React from 'react'

import {
  ItAssetsInventorySelectorDocument,
  type ItAssetsInventorySelectorQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

const PAGE_SIZE = 100

type ItAsset = NonNullable<
  ItAssetsInventorySelectorQuery['itManagement']
>['itAssetsConnection']['nodes'][number]

export const useItAssetsInStockInventorySelector = () => {
  const [allAssets, setAllAssets] = React.useState<ItAsset[]>([])
  const [isLoadingAll, setIsLoadingAll] = React.useState(false)
  const hasInitializedRef = React.useRef(false)

  const { data, loading, error, fetchMore } = useQuery(ItAssetsInventorySelectorDocument, {
    variables: {
      first: PAGE_SIZE,
    },
    fetchPolicy: 'no-cache',
  })

  const fetchAllAssets = React.useCallback(async () => {
    if (!data?.itManagement?.itAssetsConnection) return
    if (hasInitializedRef.current) return
    if (isLoadingAll) return

    hasInitializedRef.current = true

    const connection = data.itManagement.itAssetsConnection
    let allFetchedAssets = [...(connection.nodes || [])]
    let hasNextPage = connection.pageInfo?.hasNextPage
    let endCursor = connection.pageInfo?.endCursor

    setAllAssets(allFetchedAssets)

    if (!hasNextPage) {
      return
    }

    setIsLoadingAll(true)

    try {
      while (hasNextPage && endCursor) {
        const result = await fetchMore({
          variables: {
            first: PAGE_SIZE,
            after: endCursor,
          },
        })

        const newConnection = result.data?.itManagement?.itAssetsConnection
        if (newConnection?.nodes && newConnection.nodes.length > 0) {
          allFetchedAssets = [...allFetchedAssets, ...newConnection.nodes]

          hasNextPage = newConnection.pageInfo?.hasNextPage || false
          endCursor = newConnection.pageInfo?.endCursor || null
        } else {
          break
        }
      }
    } catch {
    } finally {
      setAllAssets(allFetchedAssets)
      setIsLoadingAll(false)
    }
  }, [data, fetchMore, isLoadingAll])

  React.useEffect(() => {
    fetchAllAssets()
  }, [fetchAllAssets])

  return {
    assets: allAssets,
    loading: loading || isLoadingAll,
    error,
    totalCount: data?.itManagement?.itAssetsConnection?.totalCount || 0,
  }
}
